import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import FeatureGrid from "../components/FeatureGrid";
import { useContactDialog } from "../context/ContactDialogContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1761173084851-1e5302e931fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxzeW1waG9ueSUyMG9yY2hlc3RyYSUyMGNvbmNlcnQlMjBoYWxsfGVufDB8fHx8MTc3ODY1NDcyM3ww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1766475554781-c76b4a5c704d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxjb25kdWN0b3IlMjBiYXRvbiUyMGhhbmRzfGVufDB8fHx8MTc3ODY1NDcyM3ww&ixlib=rb-4.1.0&q=85";
const FEATURES_BG =
  "https://images.unsplash.com/photo-1641824997764-be92ac96c465?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzaGVldCUyMG11c2ljJTIwY2xvc2UlMjB1cCUyMGRhcmt8ZW58MHx8fHwxNzc4NjU0NzIzfDA&ixlib=rb-4.1.0&q=85";

const VALUE_POINTS = [
  "Samlar konserter, musiker och resurser på en plats",
  "Tydlig statusuppföljning för varje del av produktionen",
  "Säker dokument- och avtalshantering",
  "Tids- och kostnadsbesparingar i administrationen",
];

const Home = () => {
  const { openDialog } = useContactDialog();
  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section
        className="relative w-full min-h-[92vh] flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1128]/95 via-[#0a1128]/85 to-[#0a1128]/55" />
        <div className="grain" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-32 w-full">
          <div className="max-w-3xl reveal">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-6"
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-8 bg-[#d4af37]" />
              Administration för orkestrar
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-white">
              Allt din orkester behöver,
              <br />
              <span className="italic gold-text">samlat på ett ställe.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
              Orkesteradmin är en webbaserad tjänst som hjälper dig att
              administrera din orkester – från konsertplanering och musiker
              till ljud, ljus, transport, boende, avtal och dokumentation.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                type="button"
                onClick={() => openDialog("demo")}
                data-testid="hero-cta-demo-button"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full"
              >
                Boka en demo <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/funktioner"
                data-testid="hero-cta-features-button"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full"
              >
                Läs mer om funktionerna
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="section-pad relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
              Om Orkesteradmin
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-white leading-tight">
              Plattformen byggd för
              <br />
              <span className="italic gold-text">orkesterns vardag.</span>
            </h2>
            <div className="gold-divider my-8" />
            <p className="text-white/75 leading-relaxed text-lg">
              Ett system som underlättar den praktiska och administrativa
              hanteringen av orkestrar och konserter. Verktyget är byggt för att
              samla allt på ett ställe – konserter, musiker, inhyrd personal,
              teknik, transporter, hotell, mat, marknadsföring, ärenden och
              avtal.
            </p>
            <ul className="mt-8 space-y-3">
              {VALUE_POINTS.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-white/80"
                  data-testid={`value-point-${i}`}
                >
                  <CheckCircle2 className="text-[#d4af37] w-5 h-5 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/funktioner"
              data-testid="about-cta-features-link"
              className="mt-10 inline-flex items-center gap-2 text-[#d4af37] hover:gap-3 transition-all"
            >
              Utforska alla funktioner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#d4af37]/20 to-transparent blur-2xl" />
              <img
                src={ABOUT_IMG}
                alt="Dirigent leder en orkester"
                className="relative rounded-2xl shadow-2xl border border-white/10 w-full object-cover aspect-[4/5]"
                data-testid="about-image"
              />
              <div className="absolute -bottom-6 -left-6 glass-card rounded-xl px-6 py-4 hidden md:block">
                <div className="text-3xl font-serif gold-text">14</div>
                <div className="text-xs uppercase tracking-wider text-white/70">
                  inbyggda moduler
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section
        className="section-pad relative overflow-hidden"
        data-testid="features-section"
      >
        <div
          className="absolute inset-0 bg-center bg-cover opacity-[0.06]"
          style={{ backgroundImage: `url(${FEATURES_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1128] via-transparent to-[#0a1128]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
              Funktioner
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-white leading-tight">
              Allt du behöver för att
              <br />
              <span className="italic gold-text">leda en orkester.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Fjorton sammankopplade moduler som tillsammans täcker hela
              produktionskedjan – från första bokning till efterarbete.
            </p>
          </div>
          <div className="mt-14">
            <FeatureGrid />
          </div>
          <div className="mt-14 text-center">
            <Link
              to="/funktioner"
              data-testid="features-cta-details-link"
              className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full"
            >
              Se alla funktioner i detalj <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-24" data-testid="cta-strip-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative glass-card rounded-2xl px-10 py-14 md:py-16 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#d4af37]/10 blur-3xl rounded-full" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                  Redo att se Orkesteradmin <span className="italic gold-text">live?</span>
                </h3>
                <p className="mt-3 text-white/70 max-w-xl">
                  Boka en kostnadsfri online-demo så visar vi hur plattformen
                  kan anpassas efter just er orkester.
                </p>
              </div>
              <button
                type="button"
                onClick={() => openDialog("demo")}
                data-testid="cta-strip-demo-button"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full whitespace-nowrap"
              >
                Boka demo <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
